import React, { useState, useEffect } from 'react';
import { ThumbsUpIcon, ClockIcon, FunnelIcon, ChatCircleIcon } from '@phosphor-icons/react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { GenerationResult } from './Slides';
import { Generation } from '../types';
import { useSendToChat } from '../hooks/useSendToChat';

interface ChallengeSubmissionsProps {
    challengeId: string;
    projectId?: string;
}

export const ChallengeSubmissions: React.FC<ChallengeSubmissionsProps> = ({ challengeId, projectId }) => {
    const { user } = useAuth();
    const handleSendToChat = useSendToChat(projectId);
    const [submissions, setSubmissions] = useState<Generation[]>([]);
    const [loading, setLoading] = useState(true);
    const [sort, setSort] = useState<'popular' | 'newest'>('popular');
    const [userVotes, setUserVotes] = useState<Set<string>>(new Set());

    useEffect(() => {
        fetchSubmissions();
        if (user) fetchUserVotes();
    }, [challengeId, sort, user]);

    const fetchUserVotes = async () => {
        if (!user) return;
        const { data } = await supabase
            .from('challenge_votes')
            .select('submission_id')
            .eq('user_id', user.id);

        if (data) {
            setUserVotes(new Set(data.map(d => d.submission_id)));
        }
    };

    const fetchSubmissions = async () => {
        setLoading(true);
        try {
            let query = supabase
                .from('challenge_submissions')
                .select(`
                    id,
                    votes_count,
                    created_at,
                    generation:generations (
                        *,
                        user:profiles (
                            username,
                            avatar_url
                        )
                    )
                `)
                .eq('challenge_id', challengeId);

            if (sort === 'popular') {
                query = query.order('votes_count', { ascending: false });
            } else {
                query = query.order('created_at', { ascending: false });
            }

            const { data } = await query;

            if (data) {
                const formatted = data.map((sub: any) => ({
                    ...sub.generation,
                    submission_id: sub.id, // Store submission ID needed for updates
                    votes_count: sub.votes_count,
                    user: Array.isArray(sub.generation.user) ? sub.generation.user[0] : sub.generation.user
                }));
                setSubmissions(formatted);
            }
        } catch (err) {
            console.error('Error fetching submissions:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleVote = async (submissionId: string, genId: string) => {
        if (!user) return; // Add login prompt later

        const hasVoted = userVotes.has(submissionId);

        // Optimistic Update
        setSubmissions(prev => prev.map(sub => {
            if ((sub as any).submission_id === submissionId) {
                return {
                    ...sub,
                    votes_count: (sub.votes_count || 0) + (hasVoted ? -1 : 1)
                };
            }
            return sub;
        }));

        const newVotes = new Set(userVotes);
        if (hasVoted) newVotes.delete(submissionId);
        else newVotes.add(submissionId);
        setUserVotes(newVotes);

        if (hasVoted) {
            // Remove Vote
            await supabase.from('challenge_votes').delete().eq('submission_id', submissionId).eq('user_id', user.id);
        } else {
            // Add Vote
            await supabase.from('challenge_votes').insert({ submission_id: submissionId, user_id: user.id });
        }
    };

    if (loading && submissions.length === 0) return <div className="text-center py-10 text-zinc-500">Loading submissions...</div>;

    return (
        <div className="mt-8 mb-16">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <FunnelIcon className="w-5 h-5 text-indigo-400" /> Community Submissions
                </h3>

                <div className="flex bg-zinc-900 rounded-lg p-1 border border-zinc-800">
                    <button
                        onClick={() => setSort('popular')}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-2 ${sort === 'popular' ? 'bg-zinc-800 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-300'}`}
                    >
                        <ThumbsUpIcon className="w-3 h-3" /> Popular
                    </button>
                    <button
                        onClick={() => setSort('newest')}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-2 ${sort === 'newest' ? 'bg-zinc-800 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-300'}`}
                    >
                        <ClockIcon className="w-3 h-3" /> Newest
                    </button>
                </div>
            </div>

            {submissions.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-zinc-800 rounded-xl bg-zinc-900/50">
                    <p className="text-zinc-500">No submissions yet. Be the first!</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {submissions.map((item: any) => (
                        <div key={item.id} className="group relative aspect-[2/3] bg-zinc-900 rounded-xl overflow-hidden border border-white/5">
                            <img
                                src={item.img}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                loading="lazy"
                            />

                            {/* Vote Overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full bg-zinc-800 overflow-hidden">
                                            {item.user?.avatar_url && <img src={item.user.avatar_url} className="w-full h-full object-cover" />}
                                        </div>
                                        <span className="text-xs text-zinc-300 font-medium truncate max-w-[80px]">@{item.user?.username}</span>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        {projectId && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleSendToChat(item.img); }}
                                                className="p-2 rounded-full backdrop-blur-md bg-white/10 hover:bg-white/20 text-white transition-colors"
                                                title="Send to chat"
                                            >
                                                <ChatCircleIcon className="w-4 h-4" />
                                            </button>
                                        )}
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleVote(item.submission_id, item.id); }}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full backdrop-blur-md transition-colors ${userVotes.has(item.submission_id) ? 'bg-indigo-500 text-white' : 'bg-white/10 hover:bg-white/20 text-white'}`}
                                        >
                                            <ThumbsUpIcon className={`w-3.5 h-3.5 ${userVotes.has(item.submission_id) ? 'fill-current' : ''}`} />
                                            <span className="text-xs font-bold">{item.votes_count || 0}</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
